import { useState } from "react";
import type { FormEvent } from "react";
import { ArrowRight, Check, Clock3, Flag, Plus, ShieldCheck } from "lucide-react";
import { Modal } from "./components";
import { PEOPLE } from "../shared/schema";
import type { Constraint, Person, Task, Workspace, Workstream } from "../shared/schema";

export function ConstraintCards({
  data,
  onEdit,
  onAdd,
}: {
  data: Workspace;
  onEdit: (value: Constraint) => void;
  onAdd: () => void;
}) {
  const [showResolved, setShowResolved] = useState(false);
  const active = data.constraints.filter((c) => showResolved || c.status === "active");
  return (
    <section className="constraints-section">
      <div className="section-heading">
        <h2>What shapes the plan</h2>
        {data.constraints.some((c) => c.status === "resolved") && (
          <button className="text-button" onClick={() => setShowResolved(!showResolved)}>
            {showResolved ? "Hide resolved" : "Show resolved"}
          </button>
        )}
        <button className="text-button" onClick={onAdd}>
          <Plus size={13} />
          Add a constraint
        </button>
      </div>
      <div className="constraint-grid">
        {active.map((c) => (
          <button
            className={`constraint-card ${c.kind === "priority" ? "personal-priority" : ""}`}
            key={c.id}
            onClick={() => onEdit(c)}
          >
            <div className="constraint-kicker">
              {c.kind === "priority" ? <ShieldCheck size={15} /> : <Flag size={15} />}{" "}
              {c.owner ? PEOPLE[c.owner].name : "Together"}
              {c.status === "resolved" ? " · RESOLVED" : ""} ·{" "}
              {c.kind === "priority" ? "PROTECTED PRIORITY" : c.kind.toUpperCase()}
            </div>
            <h3>{c.title}</h3>
            <p>{c.impact}</p>
            <div className="constraint-next">
              <span>Next step</span>
              {c.nextStep}
              <ArrowRight size={14} />
            </div>
          </button>
        ))}
        {!active.length && (
          <div className="context-note">
            No active constraints recorded. Add a missing input, capacity limit, decision, or
            personal priority when it affects the plan.
          </div>
        )}
      </div>
    </section>
  );
}
export function ConstraintEditor({
  value,
  data,
  onClose,
  onSave,
  pending,
}: {
  value: Constraint | null;
  data: Workspace;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => Promise<boolean>;
  pending: boolean;
}) {
  const [title, setTitle] = useState(value?.title ?? "");
  const [owner, setOwner] = useState<Person | "together">(value?.owner ?? "together");
  const [kind, setKind] = useState<Constraint["kind"]>(value?.kind ?? "dependency");
  const [impact, setImpact] = useState(value?.impact ?? "");
  const [nextStep, setNextStep] = useState(value?.nextStep ?? "");
  const [resolvedWhen, setResolvedWhen] = useState(value?.resolvedWhen ?? "");
  const [priorityStream, setPriorityStream] = useState(value?.priorityStreamId ?? "");
  const [streams, setStreams] = useState(value?.streamIds ?? []);
  async function save(status: Constraint["status"]): Promise<void> {
    const ok = await onSave({
      version: value?.version,
      title,
      owner: owner === "together" ? null : owner,
      kind,
      status,
      impact,
      nextStep,
      resolvedWhen,
      streamIds: streams,
      priorityStreamId: kind === "priority" && priorityStream ? priorityStream : null,
    });
    if (ok) onClose();
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={value ? "What shapes this work" : "Add a constraint"}
      description="Record the condition, what it affects, and what would resolve it. Personal details can stay in the tools where you already manage them."
      wide
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save(value?.status ?? "active");
        }}
      >
        <label className="form-label">
          Condition or priority
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={180}
          />
        </label>
        <div className="setup-grid">
          <label className="form-label">
            Owner
            <select value={owner} onChange={(e) => setOwner(e.target.value as Person | "together")}>
              <option value="together">Together</option>
              <option value="charlton">Charlton</option>
              <option value="david">David</option>
            </select>
          </label>
          <label className="form-label">
            Type
            <select value={kind} onChange={(e) => setKind(e.target.value as Constraint["kind"])}>
              <option value="dependency">Missing input</option>
              <option value="capacity">Capacity</option>
              <option value="decision">Decision</option>
              <option value="priority">Highest personal priority</option>
            </select>
          </label>
        </div>
        <label className="form-label">
          How it affects the plan
          <textarea value={impact} onChange={(e) => setImpact(e.target.value)} rows={3} />
        </label>
        <label className="form-label">
          Next meaningful step
          <textarea value={nextStep} onChange={(e) => setNextStep(e.target.value)} rows={2} />
        </label>
        <label className="form-label">
          Resolved when
          <textarea
            value={resolvedWhen}
            onChange={(e) => setResolvedWhen(e.target.value)}
            rows={2}
          />
        </label>
        {kind === "priority" && (
          <label className="form-label">
            Protect work in
            <select value={priorityStream} onChange={(e) => setPriorityStream(e.target.value)}>
              <option value="">Choose a workstream</option>
              {data.streams.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <fieldset className="dependency-picker">
          <legend>Affected workstreams</legend>
          {data.streams.map((s) => (
            <label key={s.id}>
              <input
                type="checkbox"
                checked={streams.includes(s.id)}
                onChange={(e) =>
                  setStreams(
                    e.target.checked ? [...streams, s.id] : streams.filter((id) => id !== s.id),
                  )
                }
              />
              {s.name}
            </label>
          ))}
        </fieldset>
        <div className="form-footer">
          {value && (
            <button
              type="button"
              className="button secondary"
              disabled={pending}
              onClick={() => {
                void save(value.status === "active" ? "resolved" : "active");
              }}
            >
              <Check size={14} />
              {value.status === "active" ? "Mark resolved" : "Reopen"}
            </button>
          )}
          <button className="button primary" disabled={pending || !title.trim()}>
            Save constraint
          </button>
        </div>
      </form>
    </Modal>
  );
}
const localDateInput = (value: string | null | undefined): string => {
  if (!value) return "";
  const d = new Date(value);
  const pad = (v: number): string => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export function TaskEditor({
  task,
  data,
  onClose,
  onSave,
  pending,
}: {
  task: Task | null;
  data: Workspace;
  onClose: () => void;
  onSave: (value: Record<string, unknown>) => Promise<boolean>;
  pending: boolean;
}) {
  const [title, setTitle] = useState(task?.title ?? "");
  const [brief, setBrief] = useState(task?.brief ?? "");
  const [completion, setCompletion] = useState(task?.completion ?? "");
  const [owner, setOwner] = useState<Person>(task?.owner ?? data.viewer);
  const [stream, setStream] = useState(task?.streamId ?? data.streams[0]?.id ?? "");
  const [waiting, setWaiting] = useState(task?.waitingFor ?? "");
  const [due, setDue] = useState(localDateInput(task?.dueAt));
  const [dependencies, setDependencies] = useState(task?.dependsOn ?? []);
  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (
      await onSave({
        title,
        brief,
        completion,
        owner,
        streamId: stream,
        dependsOn: dependencies,
        waitingFor: waiting.trim() || null,
        dueAt: due ? new Date(due).toISOString() : null,
        ...(task ? { version: task.version } : {}),
      })
    )
      onClose();
  }
  return (
    <Modal
      open
      onClose={onClose}
      title={task ? "Edit the work" : "Add a clear next step"}
      description="Make a direct correction without asking the AI. Dependencies stay connected to the same shared work."
      wide
    >
      <form
        onSubmit={(e) => {
          void submit(e);
        }}
      >
        <label className="form-label">
          Task title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={180}
          />
        </label>
        <div className="setup-grid">
          <label className="form-label">
            Owner
            <select value={owner} onChange={(e) => setOwner(e.target.value as Person)}>
              <option value="charlton">Charlton</option>
              <option value="david">David</option>
            </select>
          </label>
          <label className="form-label">
            Workstream
            <select
              aria-label="Workstream"
              value={stream}
              onChange={(e) => setStream(e.target.value)}
              required
            >
              {data.streams.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="form-label">
          Brief
          <textarea rows={3} value={brief} onChange={(e) => setBrief(e.target.value)} />
        </label>
        <label className="form-label">
          What done looks like
          <textarea rows={2} value={completion} onChange={(e) => setCompletion(e.target.value)} />
        </label>
        <label className="form-label">
          Waiting for an external input
          <input
            value={waiting}
            onChange={(e) => setWaiting(e.target.value)}
            placeholder="Leave blank when there is no missing external input"
          />
        </label>
        <label className="form-label">
          Deadline (optional, this device’s time zone)
          <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} />
        </label>
        <fieldset className="dependency-picker">
          <legend>Needs these tasks first</legend>
          {data.tasks
            .filter((t) => t.id !== task?.id)
            .map((t) => (
              <label key={t.id}>
                <input
                  type="checkbox"
                  checked={dependencies.includes(t.id)}
                  onChange={(e) =>
                    setDependencies(
                      e.target.checked
                        ? [...dependencies, t.id]
                        : dependencies.filter((id) => id !== t.id),
                    )
                  }
                />
                <span>
                  {t.title}
                  <small>
                    {PEOPLE[t.owner].name}
                    {t.status === "done" ? " · completed" : ""}
                  </small>
                </span>
              </label>
            ))}
        </fieldset>
        <div className="form-footer">
          <span className="form-note">Only the fields you change are protected.</span>
          <button className="button primary" disabled={pending || !title.trim() || !stream}>
            {task ? "Save changes" : "Add task"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
export function ParkTask({
  task,
  onClose,
  onSave,
  pending,
}: {
  task: Task;
  onClose: () => void;
  onSave: (value: Record<string, unknown>) => Promise<boolean>;
  pending: boolean;
}) {
  const [date, setDate] = useState("");
  return (
    <Modal
      open
      onClose={onClose}
      title="Give this some space"
      description={`Park “${task.title}” until it belongs in the plan again.`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void onSave({
            version: task.version,
            status: "parked",
            resumeAt: date ? new Date(date).toISOString() : null,
          }).then((ok) => {
            if (ok) onClose();
          });
        }}
      >
        <label className="form-label">
          Bring it back at (optional)
          <input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <p className="form-note">
          Time is entered in this device’s time zone. Leave it blank to keep the task parked until
          you bring it back.
        </p>
        <div className="form-footer">
          <button className="button primary" disabled={pending}>
            <Clock3 size={14} />
            Park task
          </button>
        </div>
      </form>
    </Modal>
  );
}
export function StreamEditor({
  stream,
  onClose,
  onSave,
  pending,
}: {
  stream: Workstream | null;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => Promise<boolean>;
  pending: boolean;
}) {
  const [name, setName] = useState(stream?.name ?? "");
  const [outcome, setOutcome] = useState(stream?.outcome ?? "");
  const [milestone, setMilestone] = useState(stream?.milestone ?? "");
  const [state, setState] = useState<Workstream["state"]>(stream?.state ?? "active");
  return (
    <Modal
      open
      onClose={onClose}
      title={stream ? "Edit the outcome" : "Add a workstream"}
      description="A workstream should have a useful outcome and a clear next milestone."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void onSave({
            name,
            outcome,
            milestone,
            state,
            version: stream?.version ?? (stream ? 1 : undefined),
          }).then((ok) => {
            if (ok) onClose();
          });
        }}
      >
        <label className="form-label">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label className="form-label">
          Outcome
          <textarea value={outcome} onChange={(e) => setOutcome(e.target.value)} rows={3} />
        </label>
        <label className="form-label">
          Next milestone
          <input value={milestone} onChange={(e) => setMilestone(e.target.value)} />
        </label>
        <label className="form-label">
          State
          <select value={state} onChange={(e) => setState(e.target.value as Workstream["state"])}>
            <option value="active">Active</option>
            <option value="parked">On the horizon</option>
          </select>
        </label>
        <div className="form-footer">
          <button className="button primary" disabled={pending || !name.trim()}>
            Save workstream
          </button>
        </div>
      </form>
    </Modal>
  );
}
